<template>
  <div class="select-input-wrapper">
    <v-select
      v-model="internalValue"
      :items="items"
      :placeholder="placeholder"
      :class="['select-input', alignClass]"
      :style="customStyle"
      :name="name"
      :menu-props="menuProps"
      solo
      flat
      single-line
      hide-details
      :height="height"
      v-bind="$attrs"
      v-on="$listeners">
      <!-- Select 아이템 커스텀 slot -->
      <template v-slot:item="{ item }">
        <slot name="item" :item="item">
          {{ getItemText(item) }}
        </slot>
      </template>

      <!-- Select 선택값 표시 커스텀 slot -->
      <template v-slot:selection="{ item }">
        <slot name="selection" :item="item">
          {{ getItemText(item) }}
        </slot>
      </template>
    </v-select>
  </div>
</template>

<script>
export default {
  name: 'SelectInput',

  inheritAttrs: false,

  props: {
    // v-model
    value: {
      type: [String, Number, Array],
      default: '',
    },

    // select 옵션 목록
    items: {
      type: Array,
      default: () => [],
    },

    // placeholder
    placeholder: {
      type: String,
      default: '',
    },

    // 텍스트 정렬: 'left', 'right',
    textAlign: {
      type: String,
      default: 'left',
      validator: value => ['left', 'right'].includes(value),
    },

    // 폰트 크기 (숫자만, px 자동 적용)
    fontSize: {
      type: Number,
      default: 16,
    },

    // 높이
    height: {
      type: [String, Number],
      default: '48',
    },

    // input name
    name: {
      type: String,
      default: '',
    },

    // select 드롭다운 메뉴 설정
    menuProps: {
      type: Object,
      default: () => ({ offsetY: true, bottom: true }),
    },
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },

    alignClass() {
      return `text-align-${this.textAlign}`;
    },

    customStyle() {
      return {
        '--input-font-size': `${this.fontSize}px`,
      };
    },
  },

  methods: {
    getItemText(item) {
      if (typeof item === 'object') {
        return item.text || item.label || item.name || item.value || '';
      }
      return item;
    },
  },
};
</script>

<style scoped lang="scss">
/* SelectInput Wrapper */
.select-input-wrapper {
  width: 100%;
}
</style>

<style lang="scss">
/* SelectInput 스타일 (scoped 없이 전역 적용) */
.select-input.v-select {
  width: 100%;

  /* v-input__control 스타일 */
  .v-input__control {
    min-height: auto !important;
  }

  .v-input__slot {
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 12px 16px !important;
    gap: 8px;
    width: 100%;
    height: 48px !important;
    min-height: 48px !important;
    background: var(--inversed, #fff) !important;
    border: 1px solid var(--disabled, #dbdbdb) !important;
    border-radius: 8px !important;
    margin-bottom: 0 !important;
    box-shadow: none !important;
  }

  /* solo + flat 조합에서 생성되는 fieldset 제거 */
  fieldset {
    border: none !important;
  }

  /* focus 상태 */
  &.v-input--is-focused {
    .v-input__slot {
      border-color: #3a3a3a !important;
    }
  }

  .v-select__selections,
  .v-select__selection,
  .v-select__selection--comma {
    font-family: 'Spoqa Han Sans Neo' !important;
    font-style: normal !important;
    font-weight: 400 !important;
    font-size: 14px !important;
    line-height: 18px !important;
    color: #3a3a3a !important;
    padding: 0 !important;
  }

  /* 값이 선택되었을 때 placeholder input 숨기기 */
  &.v-select--is-menu-active .v-select__slot input,
  &.v-input--is-dirty .v-select__slot input {
    opacity: 0 !important;
    width: 0 !important;
    height: 0 !important;
    position: absolute !important;
  }

  /* placeholder 스타일 */
  .v-select__slot input::placeholder {
    font-family: 'Spoqa Han Sans Neo';
    font-style: normal;
    font-weight: 400;
    font-size: 14px !important;
    color: var(--disabled, #dbdbdb) !important;
  }

  .v-input__append-inner {
    margin-top: 0 !important;
    align-self: center !important;

    /* 드롭다운 아이콘 색상 */
    .v-icon {
      color: #757575 !important;
    }
  }
}

/* 텍스트 정렬 */
.select-input.text-align-left .v-select__selection {
  text-align: left;
}

.select-input.text-align-right .v-select__selection {
  text-align: right;
}

.select-input.text-align-center .v-select__selection {
  text-align: center;
}

/* Select 드롭다운 메뉴 스타일 */
.v-menu__content {
  margin-top: 8px !important;

  .v-list {
    background: var(--inversed, #fff) !important;
    border: 1px solid var(--disabled_light, #f5f5f5) !important;
    border-radius: 8px !important;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1) !important;
  }

  .v-list-item {
    min-height: 30px;
    padding: 12px 16px;
    font-family: 'Spoqa Han Sans Neo';
    font-size: 14px !important;
    line-height: 18px !important;
    font-weight: 400 !important;
    color: #202020 !important;
    border-bottom: 1px solid #f5f5f5 !important;

    &:hover {
      background: var(--disabled_light, #f5f5f5) !important;
    }

    /* 선택된 아이템 스타일 */
    &.v-list-item--active {
      background: #fbfbfb !important;
      color: #202020 !important;

      /* 선택된 아이템의 체크 아이콘 색상 */
      .v-icon {
        color: #3a3a3a !important;
      }
    }

    &:last-child {
      border-bottom: none !important;
    }
  }
}
</style>
